import { useState, useEffect, useRef } from 'react';
import { calculateSunTimes, formatTimeUntil } from '../services/apiService';
import { getEstimatedLocationTime } from '../utils/timezoneHelper';

/**
 * Custom hook to manage sun times and calculations
 * 
 * @param {Object} coordinates - Latitude and longitude coordinates
 * @returns {Object} Sun times and related data
 */
const useSunTimes = (coordinates) => {
  // Track celebration to prevent multi-fire
  const celebrationFiredRef = useRef(null);

  const [times, setTimes] = useState({
    sunrise: null,
    sunset: null,
    nextEvent: null,
    nextTime: null,
    timeUntil: '',
    isDay: false,
    currentTime: new Date(),
    progress: 0,
    additionalInfo: null,
    specialCase: null,
    coordinates: null // Add coordinates to the times object
  });

  // Effect to update times every second
  useEffect(() => {
    let intervalId;
    
    const updateTimes = async () => {
      try {
        if (!coordinates || !coordinates.lat || !coordinates.lng) {
          return;
        }
        
        const { lat, lng } = coordinates;
        
        // Get the location's local time
        const locationTime = getEstimatedLocationTime(coordinates);
        
        // Get sun times (sunrise/sunset/etc)
        const sunTimes = await calculateSunTimes(lat, lng, locationTime);
        
        // Calculate time until next event using real UTC clock
        // (sunTimes.nextTime is a real UTC timestamp, so we compare with new Date())
        const timeUntil = formatTimeUntil(sunTimes.nextTime, new Date());
        
        setTimes({
          sunrise: sunTimes.sunrise,
          sunset: sunTimes.sunset,
          nextEvent: sunTimes.nextEvent,
          nextTime: sunTimes.nextTime,
          timeUntil,
          isDay: sunTimes.isDay,
          currentTime: locationTime, // Use location time
          progress: sunTimes.progress,
          specialCase: sunTimes.specialCase,
          additionalInfo: sunTimes.additionalInfo,
          coordinates // Pass the coordinates to the times object
        });
      } catch (error) {
        console.error('Error updating times:', error);
      }
    };
    
    // Initial update
    updateTimes();
    
    // Setup interval
    intervalId = setInterval(updateTimes, 1000);
    
    return () => clearInterval(intervalId);
  }, [coordinates]);

  // Determine celebration moment with single-fire protection
  const isCelebrationMoment = (() => {
    const isZero = times.timeUntil === '0h 0m 0s' && times.nextEvent;
    if (isZero && celebrationFiredRef.current !== times.nextEvent) {
      celebrationFiredRef.current = times.nextEvent;
      return true;
    }
    if (!isZero) {
      celebrationFiredRef.current = null;
    }
    return false;
  })();

  return {
    ...times,
    isCelebrationMoment
  };
};

export default useSunTimes;